import { motion } from "framer-motion";

const chapters = [
  {
    num: "01",
    title: "The commission trap",
    body: "Just Eat, Uber Eats and the rest charge up to 30% on every single order. On a CHF 40 order, that is CHF 12 gone — before food cost, before staff, before rent. The platform profits from your kitchen while you carry all the risk.",
  },
  {
    num: "02",
    title: "Own your customers",
    body: "Your customers belong to you — but on the platforms, you don't own them. You can't reach them, you can't remind them, you can't bring them back. The platform can, and it sells that access back to you. With your own system, the relationship is yours.",
  },
  {
    num: "03",
    title: "80% comes from regulars",
    body: "Most of your business comes from customers you already have. So don't struggle for new customers — re-engage the ones who already love you. With simplyOwner you own them and re-engage them automatically: push offers, newsletters, SMS and loyalty points.",
  },
  {
    num: "04",
    title: "Own the whole stack",
    body: "Homepage. Shop system. Payments. White-label iOS & Android apps. Customer data. Marketing. All under your name, on your domain, in your brand. With the no-code page builder you change everything yourself — no IT agency, no waiting.",
  },
  {
    num: "05",
    title: "One price. Yours forever.",
    body: "No monthly subscription bleeding your margin. One investment, live in less than a week, and the system is yours. Manage orders, print to the kitchen and switch every feature on or off — right from the admin app in your pocket.",
  },
];

export default function Manifesto() {
  return (
    <section id="manifesto" data-testid="manifesto-section" className="max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-40">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="font-mono text-xs tracking-[0.3em] uppercase text-brand mb-6"
            >
              The Manifesto
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-ink"
            >
              You built the business.
              <span className="block text-outline-brand mt-2">They take a cut of every order.</span>
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-12 rounded-xl overflow-hidden border border-ink/10 card-glow hidden lg:block"
            >
              <img
                src="https://images.unsplash.com/photo-1621494548002-bfc916172ead?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxyZXN0YXVyYW50JTIwY2hlZiUyMGRhcmslMjBwcmVtaXVtfGVufDB8fHx8MTc4NjgyMTc4NHww&ixlib=rb-4.1.0&q=85"
                alt="Chef in control of his kitchen"
                className="w-full aspect-[4/3] object-cover spotlight-img"
              />
            </motion.div>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-24 lg:pt-24">
          {chapters.map((c, i) => (
            <motion.article
              key={c.num}
              data-testid={`manifesto-chapter-${c.num}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.05 * i }}
              className="border-t border-ink/10 pt-10"
            >
              <div className="flex items-baseline gap-6">
                <span className="font-mono text-sm text-brand">{c.num}</span>
                <h3 className="font-display font-black uppercase tracking-tight text-2xl md:text-3xl text-ink">{c.title}</h3>
              </div>
              <p className="mt-6 text-ink/65 font-light leading-relaxed text-base md:text-lg max-w-xl lg:ml-14">{c.body}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
